import React, { useState, useMemo } from 'react';
import Seo from '../components/Seo';
import { Link } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { ShieldCheck, Search, FileCheck, ArrowRight } from 'lucide-react';
import { useCountries } from '../hooks/useCountries';
import { flagCode } from '../utils/flags';

const RegulatoryRequirementsList = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const { countries } = useCountries();

  const filteredCountries = useMemo(() => {
    if (!searchTerm) return countries;
    return countries.filter(c => 
      c.country.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [searchTerm, countries]);

  return (
    <div className="min-h-screen pt-24 pb-16">
      <Seo
        title="Phone Number Regulations & KYC by Country"
        description="The documents, address rules and KYC checks needed for virtual phone numbers in each country, and how VOIP2 collects them."
        path="/regulatory-requirements"
        breadcrumbs={[{ name: 'Regulatory requirements', path: '/regulatory-requirements' }]}
      />
      {/* Hero Section */}
      <section className="px-4 sm:px-6 lg:px-8 mb-16 bg-gradient-to-br from-[#0a1f2e] to-[#0a2f3e] py-16">
        <div className="max-w-7xl mx-auto text-center">
          <div className="flex items-center justify-center space-x-3 mb-6">
            <ShieldCheck className="w-12 h-12 text-[#c8ff00]" />
            <h1 className="text-5xl font-bold text-white">
              Regulatory Requirements
            </h1>
          </div>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            View detailed regulatory requirements and documentation needed for phone number registration in each country.
          </p>
        </div>
      </section>

      {/* Info Section */}
      <section className="px-4 sm:px-6 lg:px-8 mb-12">
        <div className="max-w-7xl mx-auto">
          <Card className="p-8 bg-gradient-to-br from-gray-50 to-white">
            <div className="flex items-start space-x-4">
              <FileCheck className="w-8 h-8 text-[#c8ff00] flex-shrink-0 mt-1" />
              <div>
                <h2 className="text-2xl font-bold mb-3" style={{ color: '#0a1f2e' }}>
                  Compliance Documentation
                </h2>
                <p className="text-gray-600 mb-4">
                  Each country has specific regulatory requirements for phone number registration. Click on any country below to view detailed requirements for:
                </p>
                <ul className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm text-gray-700">
                  <li className="flex items-center">
                    <span className="w-2 h-2 bg-[#c8ff00] rounded-full mr-2"></span>
                    Personal Identity Verification
                  </li>
                  <li className="flex items-center">
                    <span className="w-2 h-2 bg-[#c8ff00] rounded-full mr-2"></span>
                    Business Identity Verification
                  </li>
                  <li className="flex items-center">
                    <span className="w-2 h-2 bg-[#c8ff00] rounded-full mr-2"></span>
                    Address Verification
                  </li>
                </ul>
              </div>
            </div>
          </Card>
        </div>
      </section>

      {/* Countries List */}
      <section className="px-4 sm:px-6 lg:px-8 mb-16">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-8" style={{ color: '#0a1f2e' }}>
            Select a Country
          </h2>
          
          <Card className="p-6">
            {/* Search Filter */}
            <div className="mb-6">
              <div className="relative max-w-md mx-auto">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  placeholder="Search for a country..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <div className="text-sm text-gray-600 mt-3 text-center">
                Showing {filteredCountries.length} of {countries.length} countries
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredCountries.map((country, index) => {
                const countryCode = flagCode(country);
                return (
                  <Link 
                    key={index} 
                    to={`/country/${encodeURIComponent(country.country)}/regulatory-requirements`}
                    className="group"
                  >
                    <Card className="p-4 hover:shadow-lg hover:border-[#c8ff00] transition-all duration-300 cursor-pointer">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          {countryCode && (
                            <img 
                              src={`https://flagcdn.com/w40/${countryCode}.png`}
                              alt={`${country.country} flag`}
                              className="w-8 h-6 rounded shadow-sm"
                            />
                          )}
                          <span className="font-medium text-gray-900 group-hover:text-[#0a1f2e]">
                            {country.country}
                          </span>
                        </div>
                        <ArrowRight className="w-5 h-5 text-gray-400 group-hover:text-[#c8ff00] transition-colors" />
                      </div>
                    </Card>
                  </Link>
                );
              })}
            </div>

            {filteredCountries.length === 0 && (
              <div className="text-center py-12">
                <p className="text-gray-600 text-lg">No countries found matching your search.</p>
              </div>
            )}
          </Card>
        </div>
      </section>

      {/* Help Section */}
      <section className="px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <Card className="p-8 bg-gradient-to-br from-[#0a1f2e] to-[#0a2f3e] text-white">
            <div className="text-center">
              <h3 className="text-2xl font-bold mb-4">Need Help with Compliance?</h3>
              <p className="text-gray-300 mb-6">
                Our compliance team is ready to assist you with regulatory requirements and documentation.
              </p>
              <Link to="/contact">
                <button
                  className="px-6 py-3 rounded-lg font-semibold hover:opacity-90 transition-opacity"
                  style={{ backgroundColor: '#c8ff00', color: '#0a1f2e' }}
                >
                  Contact Compliance Team
                </button>
              </Link>
            </div>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default RegulatoryRequirementsList;
